/**
 * StickyMobileActionBar — Barre d'actions sticky bas mobile.
 *
 * Affichée uniquement en mobile (< lg) après que l'utilisateur ait scrollé
 * de plus de 360px (sinon la nav du haut suffit). Apparition fluide,
 * disparition lorsqu'on remonte près du sommet.
 *
 * Positionnée au-dessus du BottomNav (qui occupe ~68px + safe-area).
 *
 * Usage :
 *   <StickyMobileActionBar
 *     backTo="/enseignant/ccs-jury"
 *     backLabel="Hub CCS"
 *     cta={{ to: '/enseignant/fiche-seance-live', label: 'Séance Live', icon: Sparkles }}
 *   />
 *
 * Props :
 *   - backTo (string) : chemin pour le bouton retour rapide
 *   - backLabel (string) : libellé du bouton retour (affiché ≥ sm)
 *   - cta (object) : { to, label, icon } pour l'action principale (optionnel)
 *   - threshold (number) : seuil de scroll en px avant affichage (défaut 360)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function StickyMobileActionBar({
  backTo,
  backLabel = 'Retour',
  cta,
  threshold = 360,
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setVisible(window.scrollY > threshold);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  if (!backTo && !cta) return null;

  return (
    <div
      className={`lg:hidden fixed left-0 right-0 z-30 px-3 pointer-events-none transition-all duration-300 ease-out ${
        visible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
      }}
      data-testid="sticky-mobile-action-bar"
      aria-hidden={!visible}
    >
      <div className="pointer-events-auto rounded-2xl border-2 border-border bg-card/95 backdrop-blur shadow-lg p-2 flex items-center gap-2">
        {backTo && (
          <Link
            to={backTo}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted/40 text-foreground text-xs font-black uppercase tracking-wider hover:bg-muted active:scale-95 transition-all flex-shrink-0"
            data-testid="sticky-back"
            aria-label={`Retour : ${backLabel}`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{backLabel}</span>
          </Link>
        )}
        {cta && (
          <Link
            to={cta.to}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-black uppercase tracking-wider shadow active:scale-95 transition-all min-w-0"
            data-testid="sticky-cta"
            onClick={() => {
              if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
                navigator.vibrate(10);
              }
            }}
          >
            {cta.icon && <cta.icon className="w-4 h-4 flex-shrink-0" />}
            <span className="truncate">{cta.label}</span>
          </Link>
        )}
      </div>
    </div>
  );
}

export { StickyMobileActionBar };
